import React, { useState } from 'react';
import { Typography, Box, Stack, Chip, Grid, Paper } from '@mui/material';
import LiveEditor from '../components/Playground/LiveEditor';
import AutobiaBreadcrumbs from '../components/Navigation/AutobiaBreadcrumbs';
import CustomTabs from '../components/CustomTabs/CustomTabs';

const Navigation = () => {
  const [activeTab, setActiveTab] = useState(1);

  // Sample breadcrumb data
  const basicBreadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'Electronics', href: '/products/electronics' },
    { label: 'Laptops' },
  ];

  const ecommerceBreadcrumbs = [
    { label: 'Store', onClick: () => console.log('Navigate to Store') },
    { label: 'Categories', onClick: () => console.log('Navigate to Categories') },
    { label: 'Gaming', onClick: () => console.log('Navigate to Gaming') },
    { label: 'Gaming Laptops', onClick: () => console.log('Navigate to Gaming Laptops') },
    { label: 'ASUS ROG Strix G15' },
  ];

  const dashboardBreadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Financial', href: '/dashboard/financial' },
    { label: 'Vouchers', href: '/dashboard/financial/vouchers' },
    { label: 'Reconciliation Report' },
  ];

  // Tab data
  const navigationTabsData = [
    { id: 1, label: 'Overview' },
    { id: 2, label: 'Analytics' },
    { id: 3, label: 'Reports' },
    { id: 4, label: 'Settings' },
  ];

  // Component Examples
  const BasicBreadcrumbsExample = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
          Basic Breadcrumbs
        </Typography>
        <AutobiaBreadcrumbs items={basicBreadcrumbs} />
      </Box>
      
      <Box>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
          E-commerce Navigation
        </Typography>
        <AutobiaBreadcrumbs items={ecommerceBreadcrumbs} />
      </Box>
      
      <Box>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
          Dashboard Navigation
        </Typography>
        <AutobiaBreadcrumbs items={dashboardBreadcrumbs} />
      </Box>
    </Stack>
  );

  const NavigationTabsExample = () => (
    <Paper sx={{ p: 3, border: '1px solid', borderColor: 'gray.g200' }}>
      <CustomTabs
        tabsData={navigationTabsData}
        value={activeTab}
        onChange={setActiveTab}
      />
      <Box sx={{ mt: 3, p: 3, backgroundColor: 'gray.g25', borderRadius: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {navigationTabsData.find(tab => tab.id === activeTab)?.label} Content
        </Typography>
        <Typography variant="body2" color="gray.g600">
          This is the content area for the {navigationTabsData.find(tab => tab.id === activeTab)?.label} tab. 
          The active tab determines what content is displayed here.
        </Typography>
      </Box>
    </Paper>
  );

  const ResponsiveBreadcrumbsExample = () => (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
          Long Navigation Path (Auto-truncated)
        </Typography>
        <AutobiaBreadcrumbs 
          items={[
            { label: 'Dashboard', href: '#' },
            { label: 'E-commerce', href: '#' },
            { label: 'Product Management', href: '#' },
            { label: 'Categories', href: '#' },
            { label: 'Electronics', href: '#' },
            { label: 'Computers & Laptops', href: '#' },
            { label: 'Gaming Laptops', href: '#' },
            { label: 'High Performance', href: '#' },
            { label: 'ASUS ROG Series', href: '#' },
            { label: 'ROG Strix G15 Configuration' },
          ]}
          maxItems={4}
        />
      </Box>
      
      <Box>
        <Typography variant="body2" color="gray.g600" sx={{ mb: 1 }}>
          Custom Separator
        </Typography>
        <AutobiaBreadcrumbs 
          items={basicBreadcrumbs}
          separator=">"
        />
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Stack spacing={2} sx={{ mb: 6 }}>
        <Typography variant="h2" fontWeight={700} color="gray.g700">
          Navigation Components
        </Typography>
        <Typography variant="body1" color="gray.g600" sx={{ maxWidth: 600 }}>
          Navigation components including breadcrumbs and tab navigation 
          for clear user orientation and content organization.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip label="Breadcrumbs" color="primary" variant="outlined" />
          <Chip label="Tab Navigation" variant="outlined" />
          <Chip label="Responsive" variant="outlined" />
        </Stack>
      </Stack>

      <Stack spacing={6}>
        {/* Basic Breadcrumbs */}
        <LiveEditor
          title="Breadcrumb Navigation"
          description="Hierarchical navigation breadcrumbs showing the user's current location within the application."
          component={BasicBreadcrumbsExample}
          code={`import AutobiaBreadcrumbs from './components/Navigation/AutobiaBreadcrumbs';

// Define breadcrumb items with labels and navigation
const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Products', href: '/products' },
  { label: 'Electronics', href: '/products/electronics' },
  { label: 'Laptops' }, // Last item without navigation (current page)
];

// With click handlers instead of href
const clickableBreadcrumbs = [
  { label: 'Dashboard', onClick: () => navigate('/dashboard') },
  { label: 'Financial', onClick: () => navigate('/financial') },
  { label: 'Vouchers', onClick: () => navigate('/vouchers') },
  { label: 'Current Report' },
];

const Example = () => (
  <Stack spacing={3}>
    <AutobiaBreadcrumbs items={breadcrumbItems} />
    <AutobiaBreadcrumbs items={clickableBreadcrumbs} />
  </Stack>
);

// Props:
// items: Array<{label, href?, onClick?}> - Navigation items
// maxItems: number - Max items before truncation
// separator: ReactNode | string - Custom separator`}
        />

        {/* Tab Navigation */}
        <LiveEditor
          title="Tab Navigation"
          description="Tab-based navigation for organizing content into distinct sections within the same page."
          component={NavigationTabsExample}
          code={`import CustomTabs from './components/CustomTabs/CustomTabs';

const [activeTab, setActiveTab] = useState(1);

const tabsData = [
  { id: 1, label: 'Overview' },
  { id: 2, label: 'Analytics' },
  { id: 3, label: 'Reports' },
  { id: 4, label: 'Settings' },
];

const Example = () => (
  <Paper sx={{ p: 3 }}>
    <CustomTabs
      tabsData={tabsData}
      value={activeTab}
      onChange={setActiveTab}
    />
    
    {/* Tab Content */}
    <Box sx={{ mt: 3 }}>
      {activeTab === 1 && <OverviewContent />}
      {activeTab === 2 && <AnalyticsContent />}
      {activeTab === 3 && <ReportsContent />}
      {activeTab === 4 && <SettingsContent />}
    </Box>
  </Paper>
);

// Features:
// - Active state management
// - Keyboard navigation support
// - Material-UI integration
// - Consistent Autobia styling`}
        />

        {/* Advanced Breadcrumbs */}
        <LiveEditor
          title="Advanced Breadcrumb Features"
          description="Breadcrumbs with truncation for long paths and custom separators for different use cases."
          component={ResponsiveBreadcrumbsExample}
          code={`import AutobiaBreadcrumbs from './components/Navigation/AutobiaBreadcrumbs';

// Long navigation path that will be automatically truncated
const longPath = [
  { label: 'Dashboard', href: '#' },
  { label: 'E-commerce', href: '#' },
  { label: 'Product Management', href: '#' },
  { label: 'Categories', href: '#' },
  { label: 'Electronics', href: '#' },
  { label: 'Computers & Laptops', href: '#' },
  { label: 'Gaming Laptops', href: '#' },
  { label: 'High Performance', href: '#' },
  { label: 'ASUS ROG Series', href: '#' },
  { label: 'ROG Strix G15 Configuration' },
];

const Example = () => (
  <Stack spacing={3}>
    {/* Auto-truncated long path */}
    <AutobiaBreadcrumbs 
      items={longPath}
      maxItems={4}  // Shows first, ..., last 2 items
    />
    
    {/* Custom separator */}
    <AutobiaBreadcrumbs 
      items={basicBreadcrumbs}
      separator=">"  // or any custom ReactNode
    />
  </Stack>
);

// Advanced Features:
// - Automatic truncation with ellipsis
// - Custom separators
// - Responsive behavior
// - Accessible navigation`}
        />
      </Stack>

      {/* Usage Guidelines */}
      <Box sx={{ mt: 8, p: 4, backgroundColor: 'gray.g25', borderRadius: 2 }}>
        <Typography variant="h4" fontWeight={600} sx={{ mb: 3 }}>
          Navigation Guidelines
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Breadcrumb Best Practices
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Use breadcrumbs for hierarchical navigation with more than 2 levels. 
              Always make intermediate levels clickable and keep the current page non-clickable.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Tab Navigation
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Use tabs for organizing related content within the same context. 
              Limit to 5-7 tabs for optimal usability and consider responsive behavior.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Accessibility
            </Typography>
            <Typography variant="body2" color="gray.g600" sx={{ mb: 2 }}>
              Ensure proper ARIA labels, keyboard navigation support, and clear visual 
              indicators for active states. Use semantic HTML elements.
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
              Responsive Design
            </Typography>
            <Typography variant="body2" color="gray.g600">
              Breadcrumbs automatically truncate on smaller screens. 
              Tabs should scroll horizontally on mobile devices when needed.
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Navigation;